/// <reference path="../../../types/zoho-salesiq.d.ts" />
import { computed, useRuntimeConfig, useUser, watch } from "#imports";

export function useZoho() {
  const user = useUser();
  const runtimeConfig = useRuntimeConfig();

  watch(
    user,
    () => {
      if (typeof window !== "undefined") {
        window.$zoho?.salesiq?.visitor?.name?.(user.value?.fullName || "");
        window.$zoho?.salesiq?.visitor?.email?.(user.value?.email || "");
      }
    },
    { immediate: true }
  );

  const isB2C = computed(() => Boolean(user.value?.isB2CUser || user.value?.isB2COwner));
  const widgetCode = computed<string | undefined>(() => (isB2C.value ? runtimeConfig.public.zohoSalesIQKeyB2C : runtimeConfig.public.zohoSalesIQKey));

  function reinitZoho(key: string) {
    if (typeof window === "undefined") return;
    window.$zoho?.salesiq?.reset?.();
    // Ensure a clean slate so the next init replaces config
    Reflect.deleteProperty(window as unknown as Record<string, unknown>, "$zoho");
    document.getElementById("zsiqscript")?.remove();
    document.getElementById("zsiq_init")?.remove();
    document.getElementById("zsiq_float")?.remove();
    document.getElementById("zsiqcontainer")?.remove();

    const init = document.createElement("script");
    init.id = "zsiq_init";
    init.type = "text/javascript";
    // Force-assign salesiq object so new widgetcode is always taken
    init.innerHTML = `var $zoho=$zoho||{};$zoho.salesiq={widgetcode:"${key}",values:{},ready:function(){}};`;
    document.head.appendChild(init);

    const s = document.createElement("script");
    s.id = "zsiqscript";
    s.defer = true;
    s.src = "https://salesiq.zohopublic.com/widget";
    s.addEventListener("load", () => {
      // Apply visitor details once widget is ready
      const currentUser = user.value;
      if (!currentUser) return;
      window.$zoho?.salesiq?.visitor?.name?.(currentUser.fullName || "");
      window.$zoho?.salesiq?.visitor?.email?.(currentUser.email || "");
    });
    document.head.appendChild(s);
  }

  // Re-initialize Zoho in-place when the widget key changes.
  watch(
    widgetCode,
    (newKey, oldKey) => {
      if (!newKey || !oldKey || newKey === oldKey) return;
      reinitZoho(newKey);
    },
    { flush: "post" }
  );

  return {
    script: widgetCode.value && {
      hid: "zohosalesiq",
      type: "text/javascript",
      innerHTML: `var $zoho=$zoho || {};$zoho.salesiq = $zoho.salesiq || {widgetcode: "${widgetCode.value}", values:{},ready:function(){}};var d=document;s=d.createElement("script");s.type="text/javascript";s.id="zsiqscript";s.defer=true;s.src="https://salesiq.zohopublic.com/widget";t=d.getElementsByTagName("script")[0];t.parentNode.insertBefore(s,t);`,
    },
  };
}
